<route>{ "meta": { "moduleCode": "VEHICLE_WORK_ORDER" } }</route>
<template>
    <PageHeader :title="$t('pages.module.VEHICLE_WORK_ORDER.title')" back-to="/module/vehicle-work-order"/>

    <custom-table
        :title="$t('pages.module.VEHICLE_WORK_ORDER.title')"
        :subTitle="$t('pages.module.VEHICLE_WORK_ORDER.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        selectableRows
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        changeUrlPage
        filterComponentName="ModuleVehicleWorkOrderFilter"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange"
    >
        <template v-slot:user="{ row: record }">
            <span class="fw-bolder">{{ record.company_customer.firstname + ' ' + record.company_customer.lastname }}</span><br>
            <span class="fw-bolder fs-7">{{ record.company_customer.customer.mobile_phone ? formatPhonePretty(record.company_customer.customer.mobile_phone) : "-" }}</span>
        </template>
        <template v-slot:plate="{ row: record }">
            <Plate country-code="tr" :number="record.vehicle_snapshot?.formatted_plate_number"/>
        </template>
        <template v-slot:car="{ row: record }">
            <span class="fw-bolder">{{ record.vehicle_snapshot?.brand.name }}</span> <span class="fw-light fs-6">({{ record.vehicle_snapshot?.year.year }})</span><br>
            <span class="fw-light fs-6">{{ record.vehicle_snapshot?.brand_model.name }}</span><br>
        </template>
        <template v-slot:statusId="{ row: record }">
            <span :class="status(record.status_id)" class="badge">{{ $t('pages.module.VEHICLE_WORK_ORDER.cols.status.' + record.status_id) }}</span>
        </template>
        <template v-slot:typeName="{ row: record }">
            {{ record.type.name }}
        </template>
        <template v-slot:price="{ row: record }">
            <div class="vwo-price-cell">
                <div class="vwo-price-cell-line1">
                    <span class="vwo-price-cell-total">{{ formatTRY(record.transaction?.total_amount ?? record.total_price) }}</span>
                    <span v-if="record.transaction" class="vwo-tx-pill" :class="'vwo-tx-pill--' + record.transaction.status_id">
                        {{ $t('common.transaction.status.' + record.transaction.status_id) }}
                    </span>
                </div>
                <div v-if="record.transaction && record.transaction.status_id !== 2" class="vwo-price-cell-line2">
                    <span class="vwo-price-paid">{{ formatTRY(record.transaction.paid_amount) }}</span>
                    <span class="vwo-price-sep">/</span>
                    <span class="vwo-price-remaining">{{ formatTRY(record.transaction.remaining_amount) }}</span>
                </div>
            </div>
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.transaction.actions.openPayments')" placement="top">
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" @click="openPayments(record)">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/finance/fin010.svg"/>
                        </span>
                    </button>
                </el-tooltip>
                <el-tooltip :content="$t('pages.module.VEHICLE_WORK_ORDER.statusChange')" placement="top">
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" @click="showChangeStatus(record.id, record.status_id)">
                        <span class="svg-icon svg-icon-3">
                               <inline-svg src="/media/icons/duotune/general/gen019.svg" fill="white"/>
                        </span>
                    </button>
                </el-tooltip>
                <el-tooltip :content="$t('common.edit')" placement="top">
                    <router-link :to="'/module/vehicle-work-order/save/' + record.id" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                         <span class="svg-icon svg-icon-3">
                             <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                         </span>
                    </router-link>
                </el-tooltip>
                <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord([record.id])">
                    <template #reference>
                        <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                            </span>
                        </a>
                    </template>
                </el-popconfirm>
            </div>
        </template>
    </custom-table>

    <div class="modal fade" id="kt_modal_vehicle_work_order_status" ref="vehicleWorkOrderStatusModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ formStatus.title }}</h2>
                    <div id="kt_modal_add_customer_close" data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onSubmitChange()" :model="formStatus.data" ref="vehicleWorkOrderTypeForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div class="scroll-y me-n7 pe-7" id="kt_modal_add_customer_scroll" data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto" data-kt-scroll-dependencies="#kt_modal_add_customer_header" data-kt-scroll-wrappers="#kt_modal_add_customer_scroll" data-kt-scroll-offset="300px">
                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.module.VEHICLE_WORK_ORDER.save.cols.statusId') }}</label>
                                <el-form-item prop="status_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="formStatus.data.status_id" class="w-100" :placeholder="$t('common.chooseSelect')" filterable>
                                        <el-option v-for="(item, itemIndex) in statusList" :key="itemIndex" :value="item" :label="$t('pages.module.VEHICLE_WORK_ORDER.cols.status.' + item)"></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="formStatus.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="formStatus.loading">
                            <span v-if="!formStatus.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="formStatus.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";
import Plate from "@/components/plate/Plate.vue";
import PageHeader from "@/components/page-header/PageHeader.vue";
import { formatPhonePretty } from "@/core/services/Helper";
import transactionService from "@/components/payment/transactionService";

export default {
    name: "vehicle-work-order-type",
    components: {
        PageHeader,
        Plate,
        CustomTable
    },
    data() {
        return {
            fields: [
                {
                    name: this.$t("pages.module.VEHICLE_WORK_ORDER.cols.id"),
                    key: "id",
                    class: ''
                },
                {
                    name: this.$t("pages.module.VEHICLE_WORK_ORDER.cols.user"),
                    scopedSlots: {customRender: "user"},
                    class: "min-w-150px",
                },
                {
                    name: this.$t("pages.module.VEHICLE_WORK_ORDER.cols.plate"),
                    scopedSlots: {customRender: "plate"},
                },
                {
                    name: this.$t("pages.module.VEHICLE_WORK_ORDER.cols.customerCar"),
                    scopedSlots: {customRender: "car"},
                },
                {
                    name: this.$t("pages.module.VEHICLE_WORK_ORDER.cols.typeName"),
                    scopedSlots: {customRender: "typeName"}
                },
                {
                    name: this.$t("pages.module.VEHICLE_WORK_ORDER.cols.priceContainer"),
                    scopedSlots: {customRender: "price"}
                },
                {
                    name: this.$t("pages.module.VEHICLE_WORK_ORDER.cols.statusId"),
                    scopedSlots: {customRender: "statusId"},
                },
                {
                    name: this.$t("common.action"),
                    key: "action",
                    scopedSlots: {customRender: "actions"}
                }
            ],
            actions: [
                {
                    name: "new",
                    icon: "bi-plus-lg",
                    label: "btn.add"
                },
                {
                    name: "refresh",
                    icon: "bi-arrow-repeat",
                    label: "btn.refresh"
                },
                {
                    name: "delete",
                    icon: "bi-trash",
                    label: "btn.delete"
                }
            ],
            rules: {},
            selectedRowKeys: [],
            filterParameters: {},
            exportLoading: false,
            formStatus: {
                title: '',
                loading: false,
                updateStatus: false,
                data: {
                    id: undefined,
                    status_id: undefined,
                }
            },
            statusList: [1, 2, 3, 4, 5, 6],
        }
    },
    created() {
    },
    computed: {
        statusID() {
            return this.$route.params.id;
        },
        table() {
            return this.$store.state.module.vehicle_work_order.table;
        },
        save() {
            return this.$store.state.module.vehicle_work_order.save.data;
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t('pages.module.VEHICLE_WORK_ORDER.title'), []);

        this.refreshTable();
    },
    methods: {
        formatPhonePretty,
        formatTRY(amount) {
            const n = parseFloat(amount);
            if (isNaN(n)) return '-';
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
        },
        openPayments(record) {
            const transactionId = record?.transaction?.id ?? null
            transactionService.open({
                ...(transactionId
                    ? { transactionId }
                    : { sourceType: 'MODULE_VEHICLE_WORK_ORDER', sourceId: record?.id }),
                onChange: () => this.refreshTable(),
            })
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;

                case "new":
                    this.$router.push({
                        path: "/module/vehicle-work-order/save/"
                    });
                    break;

                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;

                default:
                    break;
            }
        },
        refreshTable() {
            this.filterParameters = {
                sort: 'created_at:desc',
                ...(this.statusID && { status_id: this.statusID }),
                ...this.filterData
            }

            this.$store.dispatch('module/vehicle_work_order/get', {
                page: {
                    current: this.$route.query.page
                },
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({}))
            });
        },
        handleTableChange(pagination, filterData) {
            this.filterParameters = Object.assign({
                sort: 'created_at:desc',
            }, filterData);

            this.$store.dispatch("module/vehicle_work_order/get", {
                page: pagination,
                filterData: this.filterParameters
            });
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        deleteRecord(id) {
            this.$store.dispatch("module/vehicle_work_order/delete", {
                id: id
            }).then((successDeleted) => {
                this.refreshTable();
            });
        },
        status(id) {
            const map = [
                "badge-light-primary",
                "badge-light-warning",
                "badge-light-success",
                "badge-light-warning",
                "badge-light-danger",
                "badge-light-danger"
            ];

            return map[id - 1] ?? "badge-light-primary";
        },
        showChangeStatus(id, status_id) {
            this.formStatus.title = this.$t('pages.module.VEHICLE_WORK_ORDER.statusChange');
            this.$store.dispatch("module/vehicle_work_order/show", {
                id: id
            }).then(() => {
                this.formStatus.data.id = id;
                this.formStatus.data.status_id = status_id;
                this.showModal(this.$refs.vehicleWorkOrderStatusModal);
            });
        },
        onSubmitChange() {
            this.$refs.vehicleWorkOrderTypeForm.validate((valid) => {
                if (valid) {
                    this.$store.dispatch('module/vehicle_work_order/changeStatus', {
                        id: this.formStatus.data.id,
                        status_id: this.formStatus.data.status_id,
                    }).then((response) => {
                        if (response?.status) {
                            this.onResponse(response, () => {
                                this.refreshTable();
                                this.hideModal(this.$refs.vehicleWorkOrderStatusModal);
                            });
                        }

                        this.formStatus.loading = false;
                    });
                } else {
                    return false;
                }
            });
        }
    },
    watch: {
        statusID(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.refreshTable();
            }
        }
    }
}
</script>

<style scoped>
.vwo-price-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 130px;
}

.vwo-price-cell-line1 {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.vwo-price-cell-total {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.vwo-price-cell-line2 {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    color: #8e8e93;
    font-variant-numeric: tabular-nums;
}

html.dark .vwo-price-cell-line2,
html.midnight .vwo-price-cell-line2 {
    color: #9A9CAE;
}

.vwo-price-paid { color: #28a745; font-weight: 600; }
.vwo-price-sep { opacity: 0.55; }
.vwo-price-remaining { color: #d97706; font-weight: 600; }

html.dark .vwo-price-paid,
html.midnight .vwo-price-paid { color: #34D399; }
html.dark .vwo-price-remaining,
html.midnight .vwo-price-remaining { color: #FBBF24; }

.vwo-tx-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 600;
    line-height: 1.2;
}

.vwo-tx-pill::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
}

.vwo-tx-pill--0 { background: rgba(107, 114, 128, 0.15); color: #6b7280; }
.vwo-tx-pill--1 { background: rgba(245, 158, 11, 0.15); color: #d97706; }
.vwo-tx-pill--2 { background: rgba(40, 167, 69, 0.15); color: #28a745; }
.vwo-tx-pill--3 { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }
.vwo-tx-pill--4 { background: rgba(239, 68, 68, 0.15); color: #ef4444; }

html.dark .vwo-tx-pill--0,
html.midnight .vwo-tx-pill--0 { color: #9CA3AF; }
html.dark .vwo-tx-pill--1,
html.midnight .vwo-tx-pill--1 { color: #FBBF24; }
html.dark .vwo-tx-pill--2,
html.midnight .vwo-tx-pill--2 { color: #34D399; }
html.dark .vwo-tx-pill--3,
html.midnight .vwo-tx-pill--3 { color: #60A5FA; }
html.dark .vwo-tx-pill--4,
html.midnight .vwo-tx-pill--4 { color: #F87171; }
</style>